import { Component, inject, Input } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  SoftwareSelectionNamedEntityDto,
  SoftwareSelectionResolutionDto,
} from 'src/app/models/device.model';
import { humanizeEnumValue } from 'src/app/utils/enum-display';
import {
  isSoftwareSelectionDeviceDependent,
  isSpadokSoftwareVersionMissing,
  isSoftwareSelectionResolutionError,
  isThirdPartySoftwareSelection,
} from 'src/app/utils/software-selection';

type DisplayableSoftwareSelectionResolution = SoftwareSelectionResolutionDto & {
  component: SoftwareSelectionNamedEntityDto;
};

interface SoftwareSelectionResolutionGroup {
  status: 'success' | 'device' | 'error';
  labelKey: string;
  resolutions: DisplayableSoftwareSelectionResolution[];
}

@Component({
  selector: 'app-effective-software-selection',
  templateUrl: './effective-software-selection.component.html',
  styleUrl: './effective-software-selection.component.scss',
  standalone: false,
})
export class EffectiveSoftwareSelectionComponent {
  private readonly translate = inject(TranslateService);

  @Input() resolutions: SoftwareSelectionResolutionDto[] | null | undefined =
    [];
  @Input() supportedProjectIds: string[] = [];
  @Input() showHeading = true;
  /** The resolutions describe a license batch, not a device. */
  @Input() forLicenseBatch = false;

  get visibleResolutions(): DisplayableSoftwareSelectionResolution[] {
    const supportedProjectIds = new Set(this.supportedProjectIds);
    return [...(this.resolutions ?? [])]
      .filter(
        (resolution): resolution is DisplayableSoftwareSelectionResolution =>
          !!resolution?.component &&
          (supportedProjectIds.size === 0 ||
            (!!resolution.component.id &&
              supportedProjectIds.has(resolution.component.id)) ||
            this.isThirdPartyResolution(resolution)),
      )
      .sort((first, second) => {
        const componentOrder = (
          first.component.name ??
          first.component.id ??
          ''
        ).localeCompare(second.component.name ?? second.component.id ?? '');
        return (
          componentOrder ||
          (first.scope?.name ?? first.scope?.id ?? '').localeCompare(
            second.scope?.name ?? second.scope?.id ?? '',
          )
        );
      });
  }

  get resolutionGroups(): SoftwareSelectionResolutionGroup[] {
    const successfulResolutions: DisplayableSoftwareSelectionResolution[] = [];
    const deviceDependentResolutions: DisplayableSoftwareSelectionResolution[] =
      [];
    const errorResolutions: DisplayableSoftwareSelectionResolution[] = [];

    for (const resolution of this.visibleResolutions) {
      if (this.isResolutionError(resolution)) {
        errorResolutions.push(resolution);
      } else if (isSoftwareSelectionDeviceDependent(resolution)) {
        deviceDependentResolutions.push(resolution);
      } else {
        successfulResolutions.push(resolution);
      }
    }

    const groups: SoftwareSelectionResolutionGroup[] = [
      {
        status: 'success',
        labelKey: 'software_override.effective.successful_components',
        resolutions: successfulResolutions,
      },
      {
        status: 'device',
        labelKey: 'software_override.effective.device_dependent_components',
        resolutions: deviceDependentResolutions,
      },
      {
        status: 'error',
        labelKey: 'software_override.effective.components_with_errors',
        resolutions: errorResolutions,
      },
    ];

    return groups.filter((group) => group.resolutions.length > 0);
  }

  getResolutionTrackKey(
    resolution: DisplayableSoftwareSelectionResolution,
    index: number,
  ): string {
    const component =
      resolution.component.id ?? resolution.component.name ?? 'component';
    const scope = resolution.scope?.id ?? resolution.scope?.name ?? 'scope';
    return `${component}:${scope}:${index}`;
  }

  getResolutionSourceLabel(resolution: SoftwareSelectionResolutionDto): string {
    switch (resolution.selectionReason) {
      case 'ARTIFACT_OVERRIDE':
        return this.translate.instant(
          'software_override.effective.source.artifact_override',
        );
      case 'LICENSE_BATCH_ARTIFACT_OVERRIDE':
        return this.translate.instant(
          'software_override.effective.source.license_batch_artifact_override',
        );
      case 'BUNDLE_OVERRIDE':
        return this.getBundleSourceLabel(
          resolution,
          'software_override.effective.source.named_bundle',
          'software_override.effective.source.bundle_override',
        );
      case 'LICENSE_BATCH_BUNDLE_OVERRIDE':
        return this.getBundleSourceLabel(
          resolution,
          'software_override.effective.source.license_batch_named_bundle',
          'software_override.effective.source.license_batch_bundle_override',
        );
      case 'LATEST_RELEASE':
        return this.translate.instant(
          'software_override.effective.source.latest_release',
        );
      case 'SCOPED_THIRD_PARTY_CLIENT':
        return this.translate.instant(
          'software_override.effective.source.scoped_third_party',
        );
      case 'DEFAULT_THIRD_PARTY_CLIENT':
        return this.translate.instant(
          'software_override.effective.source.default_third_party',
        );
      case 'DEVICE_DEDICATED_THIRD_PARTY_CLIENT':
        return this.translate.instant(
          'software_override.effective.source.device_third_party',
        );
      default:
        return this.translate.instant(
          'software_override.effective.source.resolved',
        );
    }
  }

  getResolutionResultLabel(resolution: SoftwareSelectionResolutionDto): string {
    if (
      resolution.result === 'SUCCESS' &&
      isSpadokSoftwareVersionMissing(resolution)
    ) {
      return this.translate.instant(
        'software_override.effective.result.no_version_resolved',
      );
    }

    return this.translateResolutionResult(resolution.result);
  }

  isResolutionDeviceDependent(
    resolution: SoftwareSelectionResolutionDto,
  ): boolean {
    return isSoftwareSelectionDeviceDependent(resolution);
  }

  isResolutionError(resolution: SoftwareSelectionResolutionDto): boolean {
    return isSoftwareSelectionResolutionError(resolution);
  }

  isThirdPartyResolution(resolution: SoftwareSelectionResolutionDto): boolean {
    return isThirdPartySoftwareSelection(resolution);
  }

  private getBundleSourceLabel(
    resolution: SoftwareSelectionResolutionDto,
    namedBundleKey: string,
    fallbackKey: string,
  ): string {
    const bundleName = resolution.bundleOverride?.bundle?.name;
    return bundleName
      ? this.translate.instant(namedBundleKey, { bundle: bundleName })
      : this.translate.instant(fallbackKey);
  }

  private translateResolutionResult(result?: string): string {
    const keys: Record<string, string> = {
      SUCCESS: 'software_override.effective.result.success',
      NO_LICENSE_BATCH: 'software_override.effective.result.no_license_batch',
      LICENSE_BATCH_OUTSIDE_VALIDITY_PERIOD:
        'software_override.effective.result.outside_validity_period',
      NO_LATEST_RELEASED_COMPONENT_IN_SPADOK:
        'software_override.effective.result.no_latest_release',
      NO_SPADOK_HARDWARE_COMPONENT:
        'software_override.effective.result.no_hardware_component',
      NO_THIRD_PARTY_CLIENT_CONFIGURED:
        'software_override.effective.result.no_third_party',
      THIRD_PARTY_TOKEN_REQUEST_FAILED:
        'software_override.effective.result.token_failed',
      SCOPE_NOT_FOUND: 'software_override.effective.result.scope_not_found',
      DEPENDS_ON_DEVICE: 'software_override.effective.result.depends_on_device',
    };

    if (result && keys[result]) {
      return this.translate.instant(keys[result]);
    }

    if (!result) {
      return this.translate.instant(
        'software_override.effective.result.unavailable',
      );
    }

    return humanizeEnumValue(result);
  }
}
